import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { IoCheckmarkCircle } from "react-icons/io5";
import styles from "./IntrestForm.module.css";

const baseURL = process.env.REACT_APP_BACKEND_API_URL;
const interestAPI = "api/aws/interest";

type FormData = {
  name: string;
  email: string;
  device: string;
  phone_case?: string;
  phone_case_link?: string;
  role: string;
  message?: string;
};

const deviceOptions: Record<string, string[]> = {
  iPhone: [
    "iPhone 16 Pro Max","iPhone 16 Pro","iPhone 16 Plus","iPhone 16","iPhone 16e",
    "iPhone 15 Pro Max","iPhone 15 Pro","iPhone 15 Plus","iPhone 15",
    "iPhone 14 Pro Max","iPhone 14 Pro","iPhone 14 Plus","iPhone 14",
    "iPhone 13 Pro Max","iPhone 13 Pro","iPhone 13","iPhone 13 mini",
    "iPhone 12 Pro Max","iPhone 12 Pro","iPhone 12","iPhone 12 mini",
    "iPhone SE (3rd generation, 2022)",
  ],
  Samsung: [
    "Galaxy S25 Ultra","Galaxy S25+","Galaxy S25",
    "Galaxy S24 Ultra","Galaxy S24+","Galaxy S24","Galaxy S24 FE",
    "Galaxy S23 Ultra","Galaxy S23+","Galaxy S23","Galaxy S23 FE",
    "Galaxy S22 Ultra","Galaxy S22+","Galaxy S22",
    "Galaxy S21 Ultra","Galaxy S21+","Galaxy S21","Galaxy S21 FE",
    "Galaxy Z Fold7","Galaxy Z Flip7","Galaxy Z Flip7 FE",
    "Galaxy Z Fold6","Galaxy Z Flip6","Galaxy Z Fold5","Galaxy Z Flip5",
    "Galaxy Z Fold4","Galaxy Z Flip4","Galaxy Z Fold3","Galaxy Z Flip3",
  ],
  Google: [
    "Pixel 10 Pro Fold","Pixel 10 Pro XL","Pixel 10 Pro","Pixel 10",
    "Pixel 9 Pro Fold","Pixel 9 Pro XL","Pixel 9 Pro","Pixel 9","Pixel 9a",
    "Pixel 8a","Pixel 8 Pro","Pixel 8","Pixel Fold",
    "Pixel 7a","Pixel 7 Pro","Pixel 7",
    "Pixel 6a","Pixel 6 Pro","Pixel 6","Pixel 5a (5G)",
  ],
};

export default function IntrestForm() {
  const navigate = useNavigate();

  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");

  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    device: "",
    phone_case: "",
    phone_case_link: "",
    role: "",
    message: "",
  });

  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  const handleBrandChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newBrand = e.target.value;
    setBrand(newBrand);
    setModel("");
    setFormData((p) => ({ ...p, device: "" }));
  };

  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newModel = e.target.value;
    setModel(newModel);
    setFormData((p) => ({ ...p, device: newModel }));
  };
  
  

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (!baseURL) throw new Error("Missing REACT_APP_BACKEND_API_URL");
      await axios.post(`${baseURL}${interestAPI}`, formData);
      setSubmittedEmail(formData.email);
      setSubmitted(true);                        // <-- success state triggers thank-you view
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };
  if (submitted) {
    return (
      <main id="main" className="ui-page">
        <div className="ui-container">
          <section
            className={`ui-card ui-card--lg ${styles.done}`}
            aria-labelledby="interest-done-title"
          >
            <span className={styles.doneMark} aria-hidden="true">
              <IoCheckmarkCircle size={36} />
            </span>

            <h1 id="interest-done-title">Thank you for your interest</h1>

            <p className={styles.doneCopy}>
              {submittedEmail
                ? `We have your form for ${submittedEmail}.`
                : "We have your form."}{" "}
              We will be in touch as the starter kit reaches your area.
            </p>

            <div className={styles.doneActions}>
              <button
                type="button"
                className="ui-btn"
                onClick={() => navigate("/")}
              >
                Back to home
              </button>
              <Link to="/how-it-works" className="ui-btn ui-btn--ghost">
                See how it works
              </Link>
            </div>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main id="main" className="ui-page">
      <div className="ui-container">
        <header className="ui-page-header">
          <h1>Interest form</h1>
          <p>
            Tell us about you and the phone you would use with mRehab. We will
            follow up as the starter kit reaches your area.
          </p>
        </header>

        <section className={`ui-card ui-card--lg ${styles.card}`}>
          <form onSubmit={handleSubmit} className={styles.form}>
            {error && (
              <p className="ui-banner ui-banner--error" role="alert">
                {error}
              </p>
            )}

            <div className="ui-field-grid">
              <div className="ui-field">
                <label className="ui-label" htmlFor="interest-name">
                  Name
                </label>
                <input
                  id="interest-name"
                  className="ui-input"
                  name="name"
                  autoComplete="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>

              <div className="ui-field">
                <label className="ui-label" htmlFor="interest-email">
                  Email
                </label>
                <input
                  id="interest-email"
                  className="ui-input"
                  type="email"
                  name="email"
                  autoComplete="email"
                  inputMode="email"
                  required
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            </div>

            <fieldset className={styles.group}>
              <legend className={styles.legend}>
                Which phone would you use?
              </legend>
              <div className="ui-field-grid ui-field-grid--pair">
                <div className="ui-field">
                  <label className="ui-label" htmlFor="interest-brand">
                    Brand
                  </label>
                  <select
                    id="interest-brand"
                    name="brand"
                    className="ui-input"
                    value={brand}
                    onChange={handleBrandChange}
                    required
                  >
                    <option value="" disabled>
                      Select brand
                    </option>
                    {Object.keys(deviceOptions).map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ui-field">
                  <label className="ui-label" htmlFor="interest-device">
                    Model
                  </label>
                  <select
                    id="interest-device"
                    name="device"
                    className="ui-input"
                    value={model}
                    onChange={handleModelChange}
                    disabled={!brand}
                    required
                  >
                    <option value="" disabled>
                      {brand ? "Select model" : "Pick a brand first"}
                    </option>
                    {(deviceOptions[brand] ?? []).map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </fieldset>

            <fieldset className={styles.group}>
              <legend className={styles.legend}>Phone case</legend>
              <div className="ui-field-grid">
                <div className="ui-field">
                  <label className="ui-label" htmlFor="interest-phone-case">
                    Do you use one?{" "}
                    <span className="ui-label-optional">(optional)</span>
                  </label>
                  <select
                    id="interest-phone-case"
                    name="phone_case"
                    className="ui-input"
                    value={formData.phone_case}
                    onChange={handleChange}
                  >
                    <option value="">Prefer not to say</option>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>

                {formData.phone_case === "yes" && (
                  <div className="ui-field">
                    <label
                      className="ui-label"
                      htmlFor="interest-phone-case-link"
                    >
                      Link to the case
                    </label>
                    <input
                      id="interest-phone-case-link"
                      className="ui-input"
                      name="phone_case_link"
                      type="url"
                      value={formData.phone_case_link}
                      onChange={handleChange}
                      placeholder="https://example.com/your-case"
                    />
                  </div>
                )}
              </div>
              <p className="ui-help">
                A case changes how the phone sits in a printed accessory, so it
                helps to know.
              </p>
            </fieldset>

            <div className="ui-field">
              <label className="ui-label" htmlFor="interest-role">
                Are you a patient or a health care provider?
              </label>
              <select
                id="interest-role"
                name="role"
                className="ui-input"
                value={formData.role}
                onChange={handleChange}
              >
                <option value="" disabled>
                  Select your role
                </option>
                <option value="patient">Patient</option>
                <option value="provider">Health care provider</option>
              </select>
            </div>

            <div className="ui-field">
              <label className="ui-label" htmlFor="interest-message">
                Message <span className="ui-label-optional">(optional)</span>
              </label>
              <textarea
                id="interest-message"
                className="ui-input"
                name="message"
                rows={4}
                value={formData.message}
                onChange={handleChange}
                placeholder="Any notes, therapy goals, or constraints we should know?"
              />
            </div>

            <div className={styles.actions}>
              <button
                type="submit"
                className="ui-btn ui-btn--lg"
                disabled={submitting}
                aria-busy={submitting}
              >
                {submitting ? "Submitting..." : "Submit interest"}
              </button>
              <p className="ui-help">
                We use this only to contact you about mRehab availability.
              </p>
            </div>
          </form>
        </section>
      </div>
    </main>
  );
}
